<template>
  <div class="bbp-hourly-invoice">
    <div class="bbp-hourly-invoice-header">
      <div class="bbp-hourly-invoice-header__title">{{title}}</div>
      <i class="bbp-icon-cross bbp-hourly-invoice-header__close" @click="closeForm"/>
    </div>
    <div class="bbp-hourly-invoice-body">
      <label class="bbp-hourly-invoice-body-item"
             v-if="users.length > 0"
             v-for="(user, index) in users"
             :key="user.id">
        <div class="bbp-hourly-invoice-body-item__username">
          {{user.username}}
          <div class="bbp-hourly-invoice-body-item__time">{{user.spendTime}}</div>
        </div>
        <div class="bbp-hourly-invoice-body-item-input">
          <input class="bbp-input"
                 v-autofocus="index === 0"
                 @keydown.esc="closeForm"
                 @keydown.enter="saveForm"
                 :class="{
                   'bbp-input_error': user.error
                 }"
                 v-model="user.hourlyRate"
                 @input="validateNumber(user)">
          <transition name="fade">
            <span class="bbp-hourly-invoice-body-item-input__error bbp-error-text" v-if="user.error">
              {{user.error}}
            </span>
            <span class="bbp-hourly-invoice-body-item-input__placeholder" v-else>AED</span>
          </transition>
        </div>
      </label>
      <label class="bbp-hourly-invoice-body-item"
             v-if="users.length > 0">
        <div class="bbp-hourly-invoice-body-item__username bbp-hourly-invoice-body-item__username_time">
          Amount of time spent
        </div>
        <div class="bbp-hourly-invoice-body-item-input">{{totalTime}}</div>
      </label>
      <StepsNoData
        v-if="users.length === 0"
        :viewMode="true"
      />
    </div>
    <div class="bbp-hourly-invoice-footer">
      <button class="bbp-button bbp-button_border bbp-button_border_gray" @click="closeForm">cancel</button>
      <button class="bbp-button" @click="saveForm" :disabled="hasError">generate</button>
    </div>
  </div>
</template>

<script>
import {cloneDeep, flatMap, orderBy, round, uniqBy, last} from 'lodash'
import getUser from '@/mixin/getUser'
import convertSecondsToTime from '@/mixin/convertSecondsToTime'
import getNoun from '@/mixin/getNoun'
import ProjectsApi from '@/api/Projects/api'
import {saveAs} from 'file-saver'
import moment from 'moment'
import StepsNoData from '@/components/Projects/ProjectCard/StepCard/StepsNoData'

export default {
  name: 'BBPGenerateHourlyInvoice',
  props: ['projects'],
  mixins: [getUser, convertSecondsToTime, getNoun],
  components: {StepsNoData},
  data () {
    return {
      title: 'Generate Invoice',
      users: [],
      steps: [],
      stepsByUser: []
    }
  },
  computed: {
    totalTime () {
      const time = this.steps.reduce((sum, item) => sum + item.totalTime, 0)
      return this.convertTime(time)
    },
    hasError () {
      return this.users.some(item => item.error) || this.users.length === 0
    }
  },
  methods: {
    convertTime (time) {
      const { hours, minutes } = this.convertSecondsToTime(time / 1000)
      const nameHours = this.getNoun(hours, 'hour', 'hours', 'hours')
      const nameMinutes = this.getNoun(hours, 'minute', 'minutes', 'minutes')
      return `${hours ? `${hours} ${nameHours}` : ''} ${minutes ? `${minutes} ${nameMinutes}` : ''}`
    },
    closeForm () {
      this.$emit('close')
    },
    saveForm () {
      const months = this.steps.map((item) => item.dateStart).sort((a, b) => new Date(a) - new Date(b))
      const firstMonth = moment(months[0]).format('MMM-YYYY')
      const lastMonth = moment(last(months)).format('MMM-YYYY')
      const monthNames = firstMonth === lastMonth ? firstMonth : `${firstMonth} - ${lastMonth}`
      if (!this.checkedRates() || this.hasError) return
      const body = this.getBodyForExcel()
      this.$emit('loader', true)
      ProjectsApi.getExcel(body)
        .then(resp => {
          const blob = new Blob([resp.data])
          const filename = `project report ${monthNames}.xls`
          saveAs(blob, filename)
          this.closeForm()
          this.$emit('loader', false)
        })
        .catch(err => {
          console.error(err)
          this.$emit('loader', false)
        })
    },
    getBodyForExcel () {
      const data = this.stepsByUser.map((step) => {
        const { dateStart, actionsTaken, totalTime, user = [] } = step
        const [userBody] = user
        const { _id } = userBody
        const currentUser = this.users.find((u) => u._id === _id)
        const { hourlyRateNumber = 0, username } = currentUser
        return {
          date: moment(dateStart).isValid() ? moment(dateStart).format('DD-MMM-YYYY') : '',
          assignments: actionsTaken,
          timeSpent: totalTime / 3600000,
          assignee: username.toUpperCase(),
          aedHr: hourlyRateNumber
        }
      })
      return orderBy(data, ['date'], ['asc'])
    },
    checkedRates () {
      const hasEmptyRate = this.users.every(user => user.hourlyRateNumber > 0)
      if (hasEmptyRate) return true
      this.users = this.users.map(user => {
        if (user.hourlyRateNumber <= 0) user.error = 'Can\'t be blank'
        return user
      })
    },
    validateNumber (user) {
      user.hourlyRate = this.toNumSave(user.hourlyRate)
      const result = this.toNumSave(user.hourlyRate, true)
      if (isNaN(+result)) {
        this.$emit('error', 'enter the number')
        user.error = 'enter the number'
        return ''
      } else {
        this.$emit('error', false)
        user.error = ''
      }
      user.hourlyRateNumber = round(+result, 2)
      this.users = cloneDeep(this.users)
    },
    toNumSave (value, num) {
      const regexNum = /[0-9]+|\./gmi
      const regexInput = /[0-9]+|,+|\.+/gmi
      const regex = num ? regexNum : regexInput
      if (typeof value === 'string') {
        const match = value.match(regex) || []
        return match.join('')
      }
    },
    createUserList (users) {
      const userList = uniqBy(users, '_id').map((user) => {
        const { _id } = user
        const stepsByUser = this.steps.filter(step => {
          const { user = [] } = step
          const [userBody] = user
          return userBody._id === _id
        })

        this.stepsByUser.push(...stepsByUser)
        const time = stepsByUser.reduce((sum, item) => sum + item.totalTime, 0)

        return {
          ...user,
          hourlyRate: '',
          hourlyRateNumber: 0,
          error: '',
          spendTime: this.convertTime(time)
        }
      })
      return userList
    },
    async getUsersList () {
      this.steps = flatMap(this.projects, item => item.steps)
      const users = this.steps.map(item => {
        const { user = [] } = item
        const [userBody] = user
        return userBody
      })
      const userList = this.createUserList(users)
      this.users = orderBy(userList, ['username'], ['asc'])
    }
  },
  beforeMount () {
    this.getUsersList()
  }
}
</script>

<style lang="scss" scoped>
@import "../../../sass/variables";
@import "../../../sass/mixins";

.bbp-hourly-invoice {
  @include global-font;
  display: grid;
  width: 100%;
  min-width: 521px;
  height: 100%;
  max-height: calc(100vh - 60px);
  background: $color-white;
  border-radius: 8px;

  &-header {
    padding: 15px 18px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    &__close {
      position: absolute;
      top: 10px;
      right: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: $color-silver;
      width: 26px;
      height: 26px;
      transition: 0.3s;
      cursor: pointer;

      &:hover {
        color: $color-dusty-gray
      }
    }

    &__title {
      font-weight: 500;
      font-size: 17px;
      text-align: center;
    }
  }

  &-body {
    display: grid;
    grid-gap: 20px;
    align-content: center;
    padding: 5px 18px 36px;

    &__no-data {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &-item {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: 10px;
      align-items: center;

      &__username {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        align-items: center;
        color: $color-accent;
        font-style: normal;
        font-weight: 600;
        font-size: 13px;
        line-height: 16px;
        text-transform: capitalize;
      }

      &__time {
        font-size: 11px;
        color: $color-text;
        font-weight: normal;
      }

      &-input {
        position: relative;
        display: flex;
        align-items: center;
        font-size: 14px;
        line-height: 17px;

        &__error {
          right: 8px;
        }

        &__placeholder {
          position: absolute;
          top: auto;
          right: 8px;
          color: $color-silver;
          font-weight: normal;
          font-size: 14px;
          line-height: 17px;
        }
      }
    }
  }

  &-footer {
    position: sticky;
    bottom: 0;
    display: grid;
    grid-template-columns: repeat(2, 103px);
    grid-gap: 12px;
    justify-content: end;
    padding: 18px;
    border-top: 1px solid $color-mercury;
    background: $color-alabaster;
    border-radius: 0 0 8px 8px;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/Projects/ListOfProjects/BBPGenerateHourlyInvoice.vue